/** Candidate extension of the existing named nature-template grammar. No gameplay geometry. */
export const PALETTE_KEYS = ['primary', 'shade', 'roof', 'roofEdge', 'wood', 'woodEdge', 'stone', 'iron', 'glass', 'glassCore'] as const;
export type PaletteKey = typeof PALETTE_KEYS[number];
export type Palette = Readonly<Record<PaletteKey, string>>;
export type PaletteInput = Readonly<Partial<Record<PaletteKey, string | number>>>;
export type Family = 'fallen_log' | 'dead_bracken' | 'tide_pool' | 'mist_shelf';
export interface Frame { readonly w: number; readonly h: number }
export interface Point { readonly x: number; readonly y: number }
export interface Bounds extends Point { readonly width: number; readonly height: number }
export interface CommonParams { readonly width: number; readonly height: number }
export interface LogParams extends CommonParams {
  readonly profile: 'whole' | 'split' | 'low'; readonly mossCover: number; readonly saltWear: number;
}
export interface BrackenParams extends CommonParams {
  readonly lean: number; readonly fronds: number; readonly brokenTips: number;
}
export interface PoolParams extends CommonParams {
  readonly edge: 'silt' | 'broken-stone' | 'shingle'; readonly ripples: number; readonly depth: number;
}
export interface MistParams extends CommonParams {
  readonly shelf: 'continuous' | 'broken'; readonly banks: number; readonly density: number;
}
interface Envelope<F extends Family, P> {
  readonly schema: 'helix.route-prop-recipe.v1'; readonly id: string; readonly assetId: string;
  readonly generatorId: `route.${F}.v1`; readonly template: string; readonly seed: number;
  readonly paletteId: 'coastal_salt' | 'moor_grey'; readonly detail: 'readable' | 'standard';
  readonly params: P;
}
export type Recipe = Envelope<'fallen_log', LogParams> | Envelope<'dead_bracken', BrackenParams>
  | Envelope<'tide_pool', PoolParams> | Envelope<'mist_shelf', MistParams>;
export interface RouteArtifact {
  readonly classification: 'CANDIDATE_NOT_INTEGRATED'; readonly revision: 'route-props.1';
  readonly recipe: Recipe; readonly family: Family; readonly frame: Frame;
  readonly origin: { readonly kind: 'bottom-center'; readonly point: Point };
  readonly contentBounds: Bounds; readonly paintBounds: Bounds;
  readonly boundsPolicy: 'conservative-frame-envelope';
  readonly anchors: readonly { readonly name: string; readonly point: Point; readonly role: 'support' | 'origin' | 'inspection' }[];
  /** Full-frame support span matches current scenePropSupport rule. Never a collider. */
  readonly footprint: { readonly left: Point; readonly right: Point; readonly gameplayAuthority: false;
    readonly meaning: 'conservative-current-validator-span-not-physical-support' };
  readonly paintedContact: { readonly segments: readonly { readonly left: Point; readonly right: Point }[];
    readonly meaning: 'authored-visible-underside-not-collision' };
  readonly renderRole: 'ground-dressing'; readonly gameplayGeometry: 'not-generated';
  readonly palette: Palette; readonly body: string; readonly svg: string;
  readonly recommendedRaster: Frame;
}
